// Copyright 2025. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    backups as backupApi,
    IBackupResponse,
} from 'common/api/resources/Backup';
import { useLocation } from 'react-router-dom';

interface IRestoreBackupState {
    restoreBackup: IBackupResponse | null;
    loading: boolean;
    error: Error | null;
}

export default function useRestoreBackup() {
    const { search } = useLocation();

    const restoreBackupId = React.useMemo(() => {
        const params = new URLSearchParams(search);

        const idRaw = params.get('restore_backup_id');

        if (!idRaw) {
            return null;
        }

        const idNum = Number(idRaw);

        return Number.isFinite(idNum) && idNum > 0 ? idNum : null;
    }, [search]);

    const [state, setState] = React.useState<IRestoreBackupState>({
        restoreBackup: null,
        loading: false,
        error: null,
    });

    React.useEffect(() => {
        if (!restoreBackupId) {
            setState({ restoreBackup: null, loading: false, error: null });
            return;
        }

        const fetchBackup = async (id: number) => {
            setState(prev => ({ ...prev, loading: true, error: null }));

            try {
                const backupResponse = await backupApi.item(id);

                setState({ restoreBackup: backupResponse.data.data, loading: false, error: null });
            } catch (e) {
                const err = e instanceof Error ? e : new Error('Failed to fetch backup');

                setState({ restoreBackup: null, loading: false, error: err });
            }
        };

        fetchBackup(restoreBackupId);
    }, [restoreBackupId]);

    return [state.restoreBackup];
}